/**
 * Zod input schemas: user. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { normalizePhoneE164 } from '../signatures.js';
import { z } from 'zod';
import { MAILBOXLESS_EMPLOYMENT_TYPES } from '../enums.js';
import { EligibilitySchema, EmailSchema, EmploymentTypeSchema, IsoDateStringSchema, PayoutCurrencySchema, UserRoleSchema } from './primitives.js';
/** Vacation carry-over rule (Oct 2026): none / max X days / review (HR confirms). */
export const VacationCarryOverRuleSchema = z.object({
    mode: z.enum(['none', 'max', 'review']),
    maxDays: z.number().min(0).max(365).nullable().optional(),
});
// ===========================================================================
// USER
// ===========================================================================
/** One emergency contact. Name + relationship required; phone/email optional. */
export const EmergencyContactSchema = z.object({
    name: z.string().trim().min(1).max(140),
    relationship: z.string().trim().min(1).max(60),
    phone: z.string().trim().max(40).default(''),
    email: z
        .string()
        .trim()
        .max(140)
        .nullable()
        .transform((v) => (v ? v : null)),
});
/** One child / dependent. Name required; the rest optional. */
export const ChildSchema = z.object({
    name: z.string().trim().min(1).max(140),
    lastName: z.string().trim().max(140).default(''),
    dateOfBirth: z.string().trim().max(10).default(''),
    gender: z.enum(['male', 'female', 'other']).nullable().default(null),
    ahvNumber: z.string().trim().max(40).default(''),
});
/** Fields an admin can set on a user (UI form or CSV). */
export const UserAdminEditSchema = z.object({
    email: EmailSchema,
    /**
     * Display name shown in lists, PDFs, and emails. Optional on edit:
     * if omitted on existing users, the current value is preserved.
     * If omitted on new-user creation, defaults to the local-part of
     * the email (e.g. "michael.leitner" for michael.leitner@daaily.com).
     */
    displayName: z
        .string()
        .trim()
        .min(1, 'Display name cannot be empty')
        .max(120, 'Display name too long')
        .optional(),
    payoutCurrency: PayoutCurrencySchema.nullable(),
    employmentType: EmploymentTypeSchema.nullable(),
    /** Standard user (person) or team / shared account (Oct 2026); absent = unchanged. */
    profileKind: z.enum(['person', 'shared']).nullable().optional(),
    eligibility: EligibilitySchema.nullable(),
    employeeId: z.string().trim().min(1).max(60).nullable().optional(),
    gender: z.enum(['male', 'female', 'other']).nullable().optional(),
    dateOfBirth: z.string().trim().max(10).nullable().optional(),
    maidenName: z.string().trim().max(140).nullable().optional(),
    dateOfMarriage: z.string().trim().max(10).nullable().optional(),
    ahvNumber: z.string().trim().max(40).nullable().optional(),
    taxId: z.string().trim().max(60).nullable().optional(),
    healthInsurance: z.string().trim().max(140).nullable().optional(),
    children: z.array(ChildSchema).max(20).nullable().optional(),
    partTime: z.boolean().nullable().optional(),
    fullTimeWeeklyHours: z.number().min(0).max(80).nullable().optional(),
    workingDays: z
        .object({
        mon: z.boolean(),
        tue: z.boolean(),
        wed: z.boolean(),
        thu: z.boolean(),
        fri: z.boolean(),
    })
        .nullable()
        .optional(),
    employmentStartDate: z
        .union([z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/), z.null()])
        .transform((v) => (v == null || v === '' ? null : v))
        .optional(),
    employmentEndDate: z
        .union([z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/), z.null()])
        .transform((v) => (v == null || v === '' ? null : v))
        .optional(),
    /** HR opt-in: announce in home Moments as a new joiner (from the start date on). */
    announceAsJoiner: z.boolean().nullable().optional(),
    /** New hire without a Workspace mailbox yet (forces active=false server-side). Omitted = unchanged. */
    newHire: z.boolean().nullable().optional(),
    role: UserRoleSchema,
    active: z.boolean(),
    approverEligible: z.boolean(),
    /** ISO 3166 alpha-2 country code, or null for not set. */
    country: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/, 'Must be a 2-letter ISO country code')
        .nullable(),
    /**
     * Cost center (Kostenstelle), or null. Shape-validated here; membership in
     * `config.kostenstellen` is enforced server-side in adminUpdateUser.
     */
    kostenstelle: z.string().trim().min(1).max(60).nullable(),
    /** Manager's email (reporting line). Existence + no-self-reference enforced
     *  server-side in adminUpdateUser. */
    managerEmail: EmailSchema.nullable(),
    /** Job title + team — free text, shown on the org chart + profile. */
    jobTitle: z.string().trim().min(1).max(120).nullable(),
    team: z.string().trim().min(1).max(120).nullable(),
    employmentEntity: z.string().trim().min(1).max(120).nullable().optional(),
    /** Working location (office/work base) — shown on the org chart. */
    workLocation: z.string().trim().min(1).max(120).nullable(),
    /** Assigned leave policy id (membership validated server-side). */
    absencePolicyId: z.string().trim().min(1).max(60).nullable(),
    /** Assigned bank-holiday calendar id (membership validated server-side). */
    holidayCalendarId: z.string().trim().min(1).max(60).nullable(),
    /** Access-matrix role id (membership validated server-side). */
    accessRole: z.string().trim().min(1).max(60).nullable(),
    /** Payout bank details — free text, or null. */
    accountOwnerName: z.string().trim().min(1).max(140).nullable(),
    iban: z.string().trim().min(1).max(42).nullable(),
    bicSwift: z.string().trim().min(1).max(20).nullable(),
    bankName: z.string().trim().min(1).max(140).nullable(),
    /** Postal address — free text, or null. (Country = the `country` field.) */
    address: z.string().trim().min(1).max(200).nullable(),
    zipCode: z.string().trim().min(1).max(20).nullable(),
    city: z.string().trim().min(1).max(120).nullable(),
    /** Emergency contacts (sensitive; private by default). */
    emergencyContacts: z.array(EmergencyContactSchema).max(6).nullable(),
    /** Per-year PTO carry-over balances. */
    vacationCarryOver: z
        // days may be NEGATIVE (Oct 2026): the Remote.com alignment sets carry = Remote entitlement − policy days,
        // which is below zero when Remote grants fewer days than the hub policy (Chiara −1, Tullia −0.5).
        .array(z.object({ year: z.number().int().min(2000).max(2100), days: z.number().min(-365).max(365), source: z.enum(['auto', 'hr', 'manual']).optional(), note: z.string().trim().max(300).nullish() }))
        .max(20)
        .nullable()
        .optional(),
    /** Year-end carry-over rule (Oct 2026). */
    vacationCarryOverRule: VacationCarryOverRuleSchema.nullable().optional(),
    /**
     * Real external inbox for submitter-facing emails (mailbox-less logins).
     * Shape-validated here; the MAILBOXLESS_EMPLOYMENT_TYPES requirement is
     * enforced by the cross-field superRefine below.
     */
    notificationEmail: EmailSchema.nullable(),
    /**
     * Verified private email. ADMIN edits write it directly (no verification —
     * verification is only for self-service changes on /profile). Setting it here
     * supersedes any pending self-service change (cleared server-side).
     */
    privateEmail: EmailSchema.nullable().optional(),
    /** Private / mobile phone (Oct 2026: HR sets it for a new hire, who signs their contract with a code by
     *  text message to it). Omitted = unchanged; normally self-managed on the profile. */
    privatePhone: z.string().trim().max(40).nullable().optional(),
    hubCommsUsePrivateEmail: z.boolean().nullable().optional(),
    /**
     * Per-tool access toggles. Optional on edit: omitted = "leave existing
     * tool provisioning alone". Each entry is `{ enabled, role }`.
     */
    tools: z
        .object({
        expenses: z
            .object({
            enabled: z.boolean(),
            // Accept BOTH new (ToolRole) and legacy ('employee') values.
            // Server / migration normalize on read.
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'employee', 'signatory', 'bd_viewer']),
        })
            .optional(),
        invoices: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        creditCards: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        absence: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        documents: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        orgChart: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        timeTracking: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        drivers: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        commissions: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        usl: z
            .object({
            enabled: z.boolean(),
            // 'bd_viewer' = Flex-by-BD only, own rep only (mapped via target email).
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        subscriptions: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        analytics: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        signatures: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        projects: z
            .object({
            enabled: z.boolean(),
            // 'trusted_submitter' = member whose submissions are auto-accepted.
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        newsletter: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        operations: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        outreach: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        businessCard: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
        directors: z
            .object({
            enabled: z.boolean(),
            role: z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer']),
        })
            .optional(),
    })
        .partial()
        .optional(),
    /** Analytics per-page grants (viewer role) — keys from ANALYTICS_PAGES. */
    analyticsPages: z.array(z.string().trim().min(1).max(40)).max(40).nullable().optional(),
    /** Subscriptions per-sub-nav grants (viewer role) — keys from SUBSCRIPTION_PAGES. */
    subscriptionPages: z.array(z.string().trim().min(1).max(40)).max(40).nullable().optional(),
    /** Operations per-page grants (viewer role) — keys from OPERATIONS_PAGES. */
    operationsPages: z.array(z.string().trim().min(1).max(40)).max(40).nullable().optional(),
    /** false = "No time tracking" (approver-only access). null/absent = tracks. */
    timeTracksOwn: z.boolean().nullable(),
    /** Time Tracking config (membership/format validated server-side). */
    weeklyWorkingHours: z.number().min(0).max(80).nullable(),
    /** Weekly-hours timeline (drives FTE). Each entry effectiveFrom + hours. */
    weeklyHoursHistory: z
        .array(z.object({
        effectiveFrom: IsoDateStringSchema,
        hours: z.number().min(0).max(80),
        // Working-time arrangement (part-time). Absent = full-time even split.
        partTime: z.boolean().optional(),
        workingDays: z
            .object({
            mon: z.boolean(),
            tue: z.boolean(),
            wed: z.boolean(),
            thu: z.boolean(),
            fri: z.boolean(),
        })
            .optional(),
        dayHours: z
            .object({
            mon: z.number().min(0).max(24).optional(),
            tue: z.number().min(0).max(24).optional(),
            wed: z.number().min(0).max(24).optional(),
            thu: z.number().min(0).max(24).optional(),
            fri: z.number().min(0).max(24).optional(),
        })
            .optional(),
    }))
        .max(60)
        .nullable()
        .optional(),
    timeTrackingStartDate: z
        .union([z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/), z.null()])
        .transform((v) => (v == null || v === '' ? null : v)),
    timeTrackingOpeningBalanceHours: z.number().min(-2000).max(2000).nullable(),
    /**
     * Hub-level role. Accepts the legacy 'admin' value for backward
     * compatibility; new value is 'super_admin'. Optional on edit; omitted
     * means unchanged.
     */
    hubRole: z.enum(['user', 'admin', 'super_admin']).optional(),
    /** Seed / test account — excluded from every report (hub-admin-only, like tools/hubRole). */
    isTestAccount: z.boolean().optional(),
}).superRefine((val, ctx) => {
    // A new hire has no mailbox yet: every hub email goes to the private address, so it is required.
    if (val.newHire === true && !val.privateEmail) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['privateEmail'], message: 'A private email is required for a new hire (their Workspace mailbox does not exist yet).' });
    }
    if (val.newHire === true && val.privatePhone && !normalizePhoneE164(val.privatePhone)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['privatePhone'], message: 'Enter the private phone with the country code, e.g. +41 79 123 45 67 (the contract signing code is sent there).' });
    }
    // Mailbox-less employment types (e.g. External Collaborator on a Cloud
    // Identity Free login) cannot be saved without a real notificationEmail —
    // they have no @daaily.com inbox to receive submitter-facing tool emails.
    if (val.employmentType !== null &&
        MAILBOXLESS_EMPLOYMENT_TYPES.includes(val.employmentType) &&
        !val.notificationEmail) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['notificationEmail'],
            message: `A notification email is required for employment type "${val.employmentType}" (the @daaily.com login has no mailbox).`,
        });
    }
    // External-collaborator SSO accounts (@collab.daaily.com) have no mailbox behind
    // the login — they MUST carry a private email AND route all Hub comms to it, or
    // they would receive nothing. Enforced on save (client + server share this schema).
    if (val.email.toLowerCase().split('@')[1] === 'collab.daaily.com') {
        if (!val.privateEmail) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['privateEmail'],
                message: 'A private email is required for @collab.daaily.com accounts (the login has no mailbox).',
            });
        }
        if (val.hubCommsUsePrivateEmail !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['hubCommsUsePrivateEmail'],
                message: 'Enable "Use private email for all Hub communication" for @collab.daaily.com accounts.',
            });
        }
    }
});
/**
 * CSV row schema for bulk user import.
 *
 * --- Batch 22b.2 column layout ---
 * email, displayName, payoutCurrency, employmentType, eligibility, active,
 * approverEligible, country, hubRole, expensesEnabled, expensesRole,
 * invoicesEnabled, invoicesRole
 *
 * Breaking change from the previous shape: the legacy `role` column is GONE.
 * `expensesRole` (full ToolRole enum, also accepts legacy 'employee') is
 * the required replacement. Re-export your CSV before importing.
 *
 * More permissive than UserAdminEditSchema because CSV cells arrive as
 * strings and optional fields may be empty:
 *   "" → null for optional enum fields
 *   "" / "true" / "false" → boolean for active (default true)
 */
// CSV cell helpers for the newer columns. Blank / missing → undefined =
// "leave the existing value alone" (so importing a partial CSV never wipes a
// field that isn't in the file). The import callable only writes defined ones.
const csvStr = (max) => z.preprocess((v) => (v === '' || v == null ? undefined : v), z.string().trim().min(1).max(max).optional());
const csvDate = z.preprocess((v) => (v === '' || v == null ? undefined : typeof v === 'string' ? v.trim() : v), z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional());
const csvNum = (min, max, int = false) => z.preprocess((v) => (v === '' || v == null ? undefined : Number(v)), (int ? z.number().int() : z.number()).min(min).max(max).optional());
const csvBoolOpt = z.preprocess((v) => {
    if (v === '' || v == null)
        return undefined;
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'string')
        return v.toLowerCase() === 'true';
    return Boolean(v);
}, z.boolean().optional());
const csvEmail = z.preprocess((v) => (v === '' || v == null ? undefined : String(v).trim().toLowerCase()), z.string().email().optional());
const csvRoleCell = (v) => v === '' || v == null ? undefined : typeof v === 'string' ? v.trim().toLowerCase() : v;
const csvToolRole = z.preprocess(csvRoleCell, z.enum(['submitter', 'approver', 'admin', 'approver_admin']).optional());
// Tools with their own extra role (see TOOL_ROLES): the export writes these
// values, so the import must take them back.
const csvSignaturesRole = z.preprocess(csvRoleCell, z.enum(['submitter', 'approver', 'admin', 'approver_admin', 'signatory']).optional());
const csvProjectsRole = z.preprocess(csvRoleCell, z.enum(['submitter', 'approver', 'admin', 'approver_admin', 'trusted_submitter']).optional());
const csvUslRole = z.preprocess(csvRoleCell, z.enum(['submitter', 'bd_viewer']).optional());
export const UserCsvRowSchema = z.object({
    email: EmailSchema,
    /**
     * Optional. If blank in the CSV, falls back to:
     *   existing displayName for already-imported users, or
     *   email local-part for new users.
     */
    displayName: z.preprocess((v) => (v === '' || v == null ? undefined : v), z.string().trim().min(1).max(120).optional()),
    payoutCurrency: z.preprocess((v) => (v === '' || v == null ? null : v), PayoutCurrencySchema.nullable()),
    employmentType: z.preprocess((v) => (v === '' || v == null ? null : v), EmploymentTypeSchema.nullable()),
    /** Standard user (person) or team / shared account (Oct 2026). */
    profileKind: z.preprocess((v) => (v === '' || v == null ? null : v), z.enum(['person', 'shared']).nullable()).optional(),
    eligibility: z.preprocess((v) => (v === '' || v == null ? null : v), EligibilitySchema.nullable()),
    active: z.preprocess((v) => {
        if (typeof v === 'boolean')
            return v;
        if (v === '' || v == null)
            return true;
        if (typeof v === 'string')
            return v.toLowerCase() === 'true';
        return Boolean(v);
    }, z.boolean()),
    approverEligible: z.preprocess((v) => {
        if (typeof v === 'boolean')
            return v;
        if (v === '' || v == null)
            return false;
        if (typeof v === 'string')
            return v.toLowerCase() === 'true';
        return Boolean(v);
    }, z.boolean()),
    country: z.preprocess((v) => {
        if (v === '' || v == null)
            return null;
        if (typeof v === 'string')
            return v.trim().toUpperCase();
        return v;
    }, z
        .string()
        .regex(/^[A-Z]{2}$/, 'Must be a 2-letter ISO country code')
        .nullable()),
    /**
     * Cost center (Kostenstelle). Blank → null. Membership in
     * `config.kostenstellen` is enforced server-side at import time.
     */
    kostenstelle: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(60).nullable()),
    /** Payout bank details — blank → null. */
    accountOwnerName: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(140).nullable()),
    iban: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(42).nullable()),
    bicSwift: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(20).nullable()),
    bankName: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(140).nullable()),
    /** Postal address — blank → null. */
    address: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(200).nullable()),
    zipCode: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(20).nullable()),
    city: z.preprocess((v) => (v === '' || v == null ? null : v), z.string().trim().min(1).max(120).nullable()),
    /**
     * Hub-level role. Optional in the CSV — blank means "leave existing
     * hubRole untouched on update; default to 'user' on create".
     * Accepted values: 'user', 'admin' (legacy), 'super_admin' (new).
     */
    hubRole: z.preprocess((v) => (v === '' || v == null ? undefined : v), z.enum(['user', 'admin', 'super_admin']).optional()),
    /**
     * Optional. 'true'/'false' (case-insensitive). Blank means "leave
     * existing tools.expenses.enabled untouched on update; default to true
     * on create" — so a CSV that omits the column doesn't accidentally
     * cut off everyone's access.
     */
    expensesEnabled: z.preprocess((v) => {
        if (v === '' || v == null)
            return undefined;
        if (typeof v === 'boolean')
            return v;
        if (typeof v === 'string')
            return v.toLowerCase() === 'true';
        return Boolean(v);
    }, z.boolean().optional()),
    /**
     * Per-tool expense role. REQUIRED (no default) — old CSVs with a `role`
     * column instead of `expensesRole` will fail validation.
     * Accepts the new 4-value ToolRole enum plus the legacy 'employee' value
     * (which the import callable normalizes to 'submitter' on write).
     */
    expensesRole: z.preprocess((v) => {
        if (v === '' || v == null)
            return undefined;
        if (typeof v === 'string')
            return v.trim().toLowerCase();
        return v;
    }, z.enum(['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'employee', 'signatory', 'bd_viewer'])),
    /**
     * Optional. Same blank-means-leave-alone semantics.
     * On create, blank defaults to false (no invoice-tool access by default).
     */
    invoicesEnabled: z.preprocess((v) => {
        if (v === '' || v == null)
            return undefined;
        if (typeof v === 'boolean')
            return v;
        if (typeof v === 'string')
            return v.toLowerCase() === 'true';
        return Boolean(v);
    }, z.boolean().optional()),
    /**
     * Optional. Defaults to 'submitter' on create when invoicesEnabled is
     * true. Now accepts the full 4-value ToolRole enum.
     */
    invoicesRole: z.preprocess((v) => {
        if (v === '' || v == null)
            return undefined;
        if (typeof v === 'string')
            return v.trim().toLowerCase();
        return v;
    }, z.enum(['submitter', 'approver', 'admin', 'approver_admin']).optional()),
    // ── HR Hub fields (blank → leave unchanged). ──
    employeeId: csvStr(60),
    gender: z.preprocess((v) => (v === '' || v == null ? undefined : String(v).trim().toLowerCase()), z.enum(['male', 'female', 'other']).optional()),
    dateOfBirth: csvDate,
    maidenName: csvStr(140),
    dateOfMarriage: csvDate,
    ahvNumber: csvStr(40),
    taxId: csvStr(60),
    healthInsurance: csvStr(140),
    partTime: csvBoolOpt,
    fullTimeWeeklyHours: csvNum(0, 80),
    workMon: csvBoolOpt,
    workTue: csvBoolOpt,
    workWed: csvBoolOpt,
    workThu: csvBoolOpt,
    workFri: csvBoolOpt,
    managerEmail: csvEmail,
    jobTitle: csvStr(120),
    team: csvStr(120),
    employmentEntity: csvStr(120),
    workLocation: csvStr(120),
    /** Holiday calendar by id or NAME (export writes the name to `holidayCalendar`;
     *  the legacy `holidayCalendarId` column is still accepted). */
    holidayCalendarId: csvStr(60),
    holidayCalendar: csvStr(120),
    /** Leave policy by id or NAME (`absencePolicy`, or the legacy `absencePolicyId`
     *  column). Applied like Config → Absence: written to `policyAssignments` with
     *  `absencePolicyEffectiveFrom` (blank = today, or no change when already on it),
     *  `absencePolicyId` recomputed. Hub admins only; dependent policies not supported. */
    absencePolicyId: csvStr(60),
    absencePolicy: csvStr(120),
    absencePolicyEffectiveFrom: csvDate,
    accessRole: csvStr(60),
    notificationEmail: csvEmail,
    // Private email — admin bulk-set counts as an admin edit (no verification).
    privateEmail: csvEmail,
    hubCommsUsePrivateEmail: csvBoolOpt,
    employmentStartDate: csvDate,
    employmentEndDate: csvDate,
    // Time tracking config.
    weeklyWorkingHours: csvNum(0, 80),
    timeTrackingStartDate: csvDate,
    timeTrackingOpeningBalanceHours: csvNum(-2000, 2000),
    timeTracksOwn: csvBoolOpt,
    // One PTO carry-over year (vacationCarryOver[0]).
    carryOverYear: csvNum(2000, 2100, true),
    carryOverDays: csvNum(0, 365),
    // One primary emergency contact (emergencyContacts[0]).
    emergencyName: csvStr(140),
    emergencyRelationship: csvStr(60),
    emergencyPhone: csvStr(40),
    emergencyEmail: csvStr(140),
    // One primary child (children[0]); the full list is edited in the user form.
    childName: csvStr(140),
    childLastName: csvStr(140),
    childDateOfBirth: csvDate,
    childGender: z.preprocess((v) => (v === '' || v == null ? undefined : String(v).trim().toLowerCase()), z.enum(['male', 'female', 'other']).optional()),
    childAhvNumber: csvStr(40),
    // Tool provisioning (blank → leave existing).
    creditCardsEnabled: csvBoolOpt,
    creditCardsRole: csvToolRole,
    timeTrackingEnabled: csvBoolOpt,
    timeTrackingRole: csvToolRole,
    absenceEnabled: csvBoolOpt,
    absenceRole: csvToolRole,
    orgChartEnabled: csvBoolOpt,
    orgChartRole: csvToolRole,
    documentsEnabled: csvBoolOpt,
    driversEnabled: csvBoolOpt,
    driversRole: csvToolRole,
    commissionsEnabled: csvBoolOpt,
    commissionsRole: csvToolRole,
    uslEnabled: csvBoolOpt,
    uslRole: csvUslRole,
    subscriptionsEnabled: csvBoolOpt,
    subscriptionsRole: csvToolRole,
    analyticsEnabled: csvBoolOpt,
    analyticsRole: csvToolRole,
    signaturesEnabled: csvBoolOpt,
    signaturesRole: csvSignaturesRole,
    projectsEnabled: csvBoolOpt,
    projectsRole: csvProjectsRole,
    newsletterEnabled: csvBoolOpt,
    newsletterRole: csvToolRole,
    operationsEnabled: csvBoolOpt,
    operationsRole: csvToolRole,
    /** Outreach + Business Card have a fixed role ('submitter'); no role column. */
    outreachEnabled: csvBoolOpt,
    businessCardEnabled: csvBoolOpt,
    /** HR opt-in: welcome the person in home Moments from their start date. Blank → leave existing. */
    announceAsJoiner: csvBoolOpt,
});
//# sourceMappingURL=user.js.map